import React from 'react';
import { Component } from 'react';
import './SignUpScreen.scss';
import apiRoutingService from '../../../services/apiRoutingService';
import routingService from '../../../services/routingService';
import { SimpleButton } from '../../buttons/simple/SimpleButton';
import TextField from '@mui/material/TextField';
import { Checkbox, FormControl, FormControlLabel, FormGroup, IconButton, InputAdornment } from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';

interface SignUpScreenProps {
}

interface SignUpScreenState {
    name: string,
    email: string;
    password: string;
    password2: string;
    termsConsent: boolean;
    newsletterConsent: boolean;
    message: string;
    termsMessage: string;
    isSuccess: boolean;
    isLoading: boolean;
    nameError: boolean;
    emailError: boolean;
    passwordError: boolean;
    password2Error: boolean;
    nameMessage: string;
    emailMessage: string;
    passwordMessage: string;
    password2Mesage: string;
    showPassword: boolean;
    showPassword2: boolean;
}

export default class SignUpScreen extends Component<SignUpScreenProps, SignUpScreenState> {
    constructor(props: SignUpScreenProps) {
        super(props);

        this.state = {
            name: "",
            email: "",
            password: "",
            password2: "",
            message: "",
            termsMessage: "",
            isSuccess: true,
            isLoading: false,
            termsConsent: false,
            newsletterConsent: false,
            showPassword: false,
            showPassword2: false,

            nameError: false,
            emailError: false,
            passwordError: false,
            password2Error: false,
            nameMessage: "",
            emailMessage: "",
            passwordMessage: "",
            password2Mesage: ""
        }

        this.signUp = this.signUp.bind(this);
        this.validateName = this.validateName.bind(this);
        this.validateEmail = this.validateEmail.bind(this);
        this.validatePassword = this.validatePassword.bind(this);
        this.validatePassword2 = this.validatePassword2.bind(this);
    }

    readonly errorFromServer = "Coś poszło nie tak, spróbuj za chwilę";
    readonly buttonText = "Stwórz konto";
    readonly buttonLoadingText = "Rejestruję...";
    readonly dontRememberPass = "Nie pamiętam hasła";
    readonly passwordRequirements = "Hasło powinno mieć minimum sześć znaków, cyfrę, małą i wielką literę";
    readonly samePasswords = "Hasła w obu polach muszą być identyczne";
    readonly noTermsConsent = "Należy zaakceptować regulamin";
    readonly wrongNameLength = "Twoje imię powinno mieć między 1 a 20 znaków";
    readonly wrongEmail = "Błędny adres email";
    readonly successfulRegistrationMessage = "Konto zostało utworzone";

    private async signUp(e: any) {
        e.preventDefault();
        let { email, password, name, termsConsent, newsletterConsent } = this.state;
        let shouldSendRequest = this.canSignUp();
        let message = "";
        let termsMessage = "";

        if (!termsConsent) {
            shouldSendRequest = false;
            termsMessage = this.noTermsConsent;
        }

        if (shouldSendRequest) {
            let isOk = await this.sendRequest(name, email, password, termsConsent, newsletterConsent);

            if (!isOk) {
                this.setState({ isSuccess: false, message: this.errorFromServer });
                return;
            }

            message = this.successfulRegistrationMessage;
            this.setState({ password: "", password2: "", isSuccess: true, message, termsMessage });

            await apiRoutingService.logIn(email, password);
            window.location.replace(routingService.getUserAccountStatusPageRoute())
        } else {
            this.setState({ isSuccess: false, message, termsMessage });
        }
    }

    private canSignUp(): boolean {
        this.validateEmail();
        this.validateName();
        this.validatePassword();
        this.validatePassword2();

        let { email, password, password2, name, emailError, passwordError, password2Error, nameError } = this.state;

        return (
            email.length > 0 &&
            password.length > 0 &&
            password2.length > 0 &&
            name.length > 0 &&
            !emailError &&
            !nameError &&
            !passwordError &&
            !password2Error
        );
    }

    private async sendRequest(name: string, email: string, password: string, termsConsent: boolean, newsletterConsent: boolean): Promise<boolean> {
        let result = false;

        this.setState({ isLoading: true });
        try {
            result = await apiRoutingService.signUp(email, password, name, termsConsent, newsletterConsent);
        }
        catch { }
        this.setState({ isLoading: false });
        return result;
    }

    private validateName() {
        let { name } = this.state;
        let error = false;
        let message = "";

        if (name.length < 1 || name.length > 20) {
            error = true;
            message = this.wrongNameLength;
        }

        this.setState({
            nameError: error,
            nameMessage: message
        });

    }

    private validatePassword() {
        let { password } = this.state;
        let message = "";
        let error = false;

        if (!this.isPasswordValid(password)) {
            error = true;
            message = this.passwordRequirements;
        }

        this.setState({
            passwordError: error,
            passwordMessage: message
        });
    }

    private validatePassword2() {
        let { password, password2 } = this.state;
        let message = "";
        let error = false;

        if (password !== password2) {
            error = true;
            message = this.samePasswords;
        }

        this.setState({
            password2Error: error,
            password2Mesage: message
        });
    }

    private validateEmail() {
        let { email } = this.state;
        let error = false;
        let message = "";

        if (!this.isEmailValid(email)) {
            error = true;
            message = this.wrongEmail;
        }

        this.setState({
            emailError: error,
            emailMessage: message
        });
    }

    private isEmailValid(value: unknown): boolean {
        if (value === null || value === undefined) {
            return true;
        }

        if (typeof value !== "string") {
            return false;
        }

        let email = value as string;

        if (email.includes("\r") || email.includes("\n")) {
            return false;
        }

        const index = email.indexOf("@");

        return (
            index > 0 &&
            index !== email.length - 1 &&
            index === email.lastIndexOf("@")
        );
    }

    private isPasswordValid(password: string): boolean {
        const minLength = 6;
        const regexLowercase = /[a-z]/;
        const regexUppercase = /[A-Z]/;
        const regexDigit = /\d/;

        return (
            password.length >= minLength &&
            regexLowercase.test(password) &&
            regexUppercase.test(password) &&
            regexDigit.test(password)
        );
    }

    public render() {
        let { isSuccess, message, termsMessage, email, password, isLoading, name, password2,
            termsConsent, newsletterConsent, showPassword, showPassword2 } = this.state;
        let messageCss = isSuccess ? "message message-success" : "message message-error";

        let inputStyles = {
            input: { color: "white" },
            label: { color: "white" },
            "& .MuiInput-underline:before": {
                borderBottomColor: "white",
            },
            "& .MuiInput-underline:hover:before": {
                borderBottomColor: "white !important",
            },
            "& .MuiInput-underline:after": {
                borderBottomColor: "white",
            },
            "& .Mui-error:before, & .Mui-error:after": {
                borderBottomColor: "red !important",
            },
        };

        return (
            <div className="sign-up-container">
                <div className="row">
                    <div className="col">
                        <form id="sing-up-form">

                            <h1>Utwórz konto</h1>

                            <div className="form-textfields">
                                <TextField
                                    label="Twoje imię (max 20 znaków)"
                                    required
                                    error={this.state.nameError}
                                    helperText={this.state.nameMessage}
                                    variant="standard"
                                    type='text'
                                    value={name}
                                    onChange={(e) => this.setState({ name: e.target.value })}
                                    onBlur={(e) => this.validateName()}
                                    sx={inputStyles}
                                />
                                <br />

                                <TextField
                                    label="Adres email"
                                    required
                                    error={this.state.emailError}
                                    helperText={this.state.emailMessage}
                                    variant="standard"
                                    type='email'
                                    value={email}
                                    onChange={(e) => this.setState({ email: e.target.value })}
                                    onBlur={(e) => this.validateEmail()}
                                    sx={inputStyles}
                                />
                                <br />

                                <TextField
                                    label="Hasło"
                                    required
                                    error={this.state.passwordError}
                                    helperText={this.state.passwordMessage}
                                    variant="standard"
                                    type={showPassword ? "text" : "password"}
                                    value={password}
                                    onChange={(e) => this.setState({ password: e.target.value })}
                                    onBlur={(e) => this.validatePassword()}
                                    sx={inputStyles}
                                    slotProps={{
                                        input: {
                                            endAdornment: (
                                                <InputAdornment
                                                    position="end"
                                                    className="show-password-icon"
                                                >
                                                    <IconButton
                                                        onClick={() => this.setState({ showPassword: !showPassword })}
                                                        onMouseDown={(e) => e.preventDefault()}
                                                        edge="end"
                                                        sx={{ color: "white" }}
                                                    >
                                                        {showPassword ? <Visibility /> : <VisibilityOff />}
                                                    </IconButton>
                                                </InputAdornment>
                                            ),
                                        },
                                    }}
                                />
                                <br />

                                <TextField
                                    label="Powtórz hasło"
                                    required
                                    error={this.state.password2Error}
                                    helperText={this.state.password2Mesage}
                                    variant="standard"
                                    type={showPassword2 ? "text" : "password"}
                                    value={password2}
                                    onChange={(e) => this.setState({ password2: e.target.value })}
                                    onBlur={(e) => this.validatePassword2()}
                                    sx={inputStyles}
                                    slotProps={{
                                        input: {
                                            endAdornment: (
                                                <InputAdornment
                                                    position="end"
                                                    className="show-password-icon"
                                                >
                                                    <IconButton
                                                        onClick={() => this.setState({ showPassword2: !showPassword2 })}
                                                        onMouseDown={(e) => e.preventDefault()}
                                                        edge="end"
                                                        sx={{ color: "white" }}
                                                    >
                                                        {showPassword2 ? <Visibility /> : <VisibilityOff />}
                                                    </IconButton>
                                                </InputAdornment>
                                            ),
                                        },
                                    }}
                                />
                                <br />

                                <div className="terms-group">
                                    <FormGroup>
                                        <FormControlLabel
                                            required
                                            control={<Checkbox
                                                value={termsConsent}
                                                onChange={(e) => this.setState({ termsConsent: !termsConsent })}
                                                sx={{
                                                    color: '#fff',
                                                    '&.Mui-checked': {
                                                        color: '#fff'
                                                    }
                                                }}
                                            />}
                                            label={
                                                <span className="checkbox-text">
                                                    Akceptuję Regulamin świadczenia usług oraz Politykę prywatności. Wyrażam też zgodę na przetwarzanie moich danych osobowych.
                                                </span>
                                            }
                                        />
                                        <a className="terms-link" href={routingService.getTermsPageRoute()}>Link do regulaminu</a>

                                        <div className="form-group">
                                            <p className='validation-message-terms'>{termsMessage}</p>
                                        </div>
                                        
                                        <FormControlLabel
                                            control={<Checkbox
                                                value={newsletterConsent}
                                                onChange={(e) => this.setState({ newsletterConsent: !newsletterConsent })}
                                                sx={{
                                                    color: '#fff',
                                                    '&.Mui-checked': {
                                                        color: '#fff'
                                                    }
                                                }}
                                            />}
                                            label={
                                                <span className="checkbox-text">
                                                    Chcę być imformowany o promocjach i nowościach w Mediti. W każdej chwili możesz zrezygnoweać w tej opcji.
                                                </span>}
                                        />
                                    </FormGroup>
                                </div>
                            </div>

                            <div className="sign-up-button">
                                <SimpleButton
                                    text={isLoading ? this.buttonLoadingText : this.buttonText}
                                    onClick={(e) => this.signUp(e)}
                                />
                            </div>

                            <div className="form-group">
                                <p className={messageCss}>{message}</p>
                            </div>

                            <a href={routingService.getLogInPageRoute()} className="go-to-login gray-link">
                                Masz już konto? Zaloguj się!
                            </a>
                        </form>
                    </div>
                </div >
            </div >
        )
    }
}